import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Slider } from "@/components/ui/slider";
import { Checkbox } from "@/components/ui/checkbox";
import { Separator } from "@/components/ui/separator";
import { ProfileFormData } from "@/pages/ProfileCreation";
import { Loader2 } from "lucide-react";
import { useState, useEffect, useRef } from "react";
import { DIETARY_PREFERENCES, SMOKING, EXERCISE_FREQUENCY } from "@/lib/enums";

const schema = z.object({
  personality_type: z.number().min(1, "Personality type must be at least 1").max(10, "Personality type must be 10 or less"),
  personality_description: z.string().trim().min(10, "Please describe your personality (at least 10 characters)").max(2000, "Personality description must be 2000 characters or less"),
  dietary_preferences: z.array(z.string()).min(1, "Select at least one dietary preference"),
  smoking: z.string().min(1, "Please indicate smoking status"),
  exercise_routine: z.string().optional(),
  bio: z.string().trim().min(50, "Bio must be at least 50 characters").max(5000, "Bio must be 5000 characters or less"),
});

// SSoT order is low→high; form displays high→low for UX
const EXERCISE_OPTIONS = [...EXERCISE_FREQUENCY].reverse();

interface Props {
  data: ProfileFormData;
  onComplete: (data: Partial<ProfileFormData>) => void;
  onBack: () => void;
  saving: boolean;
  continueLabel?: string;
  loadingLabel?: string;
}

export function Step4LifestylePersonality({ data, onComplete, onBack, saving, continueLabel = "Save & Continue", loadingLabel = "Saving..." }: Props) {
  const [bioLength, setBioLength] = useState(data.bio?.length || 0);
  const [personalityLength, setPersonalityLength] = useState(data.personality_description?.length || 0);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      personality_type: data.personality_type || 5,
      personality_description: data.personality_description || "",
      dietary_preferences: data.dietary_preferences || [],
      smoking: data.smoking || "",
      exercise_routine: data.exercise_routine,
      bio: data.bio || "",
    },
  });

  // Hydrate once on mount / when re-entering this step. Do NOT reset on every
  // parent re-render — that clobbered in-flight user input (bug B2).
  const hasHydrated = useRef(false);
  useEffect(() => {
    if (hasHydrated.current) return;
    hasHydrated.current = true;
    reset({
      personality_type: data.personality_type || 5,
      personality_description: data.personality_description || "",
      dietary_preferences: data.dietary_preferences || [],
      smoking: data.smoking || "",
      exercise_routine: data.exercise_routine,
      bio: data.bio || "",
    });
    setBioLength(data.bio?.length || 0);
    setPersonalityLength(data.personality_description?.length || 0);
  }, [data, reset]);

  const personalityType = watch("personality_type");
  const dietaryPreferences = watch("dietary_preferences");

  const getPersonalityLabel = (value: number) => {
    if (value <= 3) return "Introverted";
    if (value <= 7) return "Ambivert";
    return "Extroverted";
  };

  const toggleDietaryPreference = (value: string) => {
    const current = dietaryPreferences || [];
    if (current.includes(value)) {
      setValue("dietary_preferences", current.filter(v => v !== value));
    } else {
      setValue("dietary_preferences", [...current, value]);
    }
  };

  const onSubmit = (formData: any) => {
    onComplete(formData);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
      <div className="space-y-6">
        <h3 className="text-lg font-semibold text-foreground">Personality</h3>
        
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <Label>Personality type *</Label>
            <span className="text-sm font-medium text-primary">
              {personalityType} - {getPersonalityLabel(personalityType)}
            </span>
          </div>
          <Slider
            value={[personalityType]}
            onValueChange={(value) => setValue("personality_type", value[0])}
            min={1}
            max={10}
            step={1}
            className="w-full"
          />
          <div className="flex justify-between text-xs text-muted-foreground">
            <span>1 - Very Introverted</span>
            <span>5 - Ambivert</span>
            <span>10 - Very Extroverted</span>
          </div>
        </div>

        <div className="space-y-2">
          <div className="flex justify-between items-center">
            <Label htmlFor="personality_description">Personality description *</Label>
            <span className="text-sm text-muted-foreground">{personalityLength}/2000</span>
          </div>
          <Textarea
            id="personality_description"
            {...register("personality_description")}
            placeholder="Describe your personality, hobbies, interests..."
            rows={4}
            maxLength={2000}
            onChange={(e) => {
              register("personality_description").onChange(e);
              setPersonalityLength(e.target.value.length);
            }}
          />
          <p className="text-sm text-muted-foreground">
            What makes you unique? What do you enjoy doing in your free time?
          </p>
          {errors.personality_description && (
            <p className="text-sm text-destructive">{errors.personality_description.message}</p>
          )}
        </div>
      </div>

      <Separator />

      <div className="space-y-6">
        <h3 className="text-lg font-semibold text-foreground">Lifestyle</h3>
        
        <div className="space-y-3">
          <Label>Dietary preferences *</Label>
          <div className="space-y-2">
            {DIETARY_PREFERENCES.map((option) => (
              <div key={option} className="flex items-center space-x-2">
                <Checkbox
                  id={`dietary-${option}`}
                  checked={dietaryPreferences?.includes(option)}
                  onCheckedChange={() => toggleDietaryPreference(option)}
                />
                <Label
                  htmlFor={`dietary-${option}`}
                  className="font-normal cursor-pointer"
                >
                  {option}
                </Label>
              </div>
            ))}
          </div>
          {errors.dietary_preferences && (
            <p className="text-sm text-destructive">{errors.dietary_preferences.message}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="smoking">Do you smoke? *</Label>
          <Select value={watch("smoking")} onValueChange={(value) => setValue("smoking", value)}>
            <SelectTrigger>
              <SelectValue placeholder="Select your smoking status" />
            </SelectTrigger>
            <SelectContent>
              {SMOKING.map((option) => (
                <SelectItem key={option} value={option}>{option}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {errors.smoking && (
            <p className="text-sm text-destructive">{errors.smoking.message}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="exercise_routine">Exercise/fitness routine (optional)</Label>
          <Select value={watch("exercise_routine") || ""} onValueChange={(value) => setValue("exercise_routine", value)}>
            <SelectTrigger>
              <SelectValue placeholder="Select your exercise frequency" />
            </SelectTrigger>
            <SelectContent>
              {EXERCISE_OPTIONS.map((option) => (
                <SelectItem key={option} value={option}>{option}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <Separator />

      <div className="space-y-6">
        <h3 className="text-lg font-semibold text-foreground">About You</h3>
        
        <div className="space-y-2">
          <div className="flex justify-between items-center">
            <Label htmlFor="bio">Bio *</Label>
            <span className="text-sm text-muted-foreground">{bioLength}/5000</span>
          </div>
          <Textarea
            id="bio"
            {...register("bio")}
            placeholder="Tell potential matches about yourself, your values, goals, what you're passionate about..."
            rows={6}
            maxLength={5000}
            onChange={(e) => {
              register("bio").onChange(e);
              setBioLength(e.target.value.length);
            }}
          />
          <p className="text-sm text-muted-foreground">
            This is your chance to showcase who you are. Share your interests, aspirations, and what matters most to you.
          </p>
          {errors.bio && (
            <p className="text-sm text-destructive">{errors.bio.message}</p>
          )}
        </div>
      </div>

      <div className="flex justify-between gap-4">
        <Button type="button" variant="outline" onClick={onBack}>
          Back
        </Button>
        <Button type="submit" disabled={saving} className="min-w-32">
          {saving ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              {loadingLabel}
            </>
          ) : (
            continueLabel
          )}
        </Button>
      </div>
    </form>
  );
}
